import { GraphModelShape } from '@ardoq/data-model';
import { GraphInterface } from '../graphInterface/graphInterface';
import { ArdoqId, GroupingRule, GroupType } from '@ardoq/api-types';
import { buildGroups } from '../utils/grouping/buildGroups';
import { getResultingReferenceList } from './utils';
import { RawGraphNode2 } from '../types';
import { TraversalAndGroupingResult } from '../traversal/types';
import { type I18nLocale } from '@ardoq/i18n';

const complexGroups = new Set([
  GroupType.REFERENCE,
  GroupType.PARENT,
  GroupType.PARENT_ALL,
]);

const groupMapHasClones = (groupMap: Map<string, RawGraphNode2>): boolean => {
  const componentsUsedAsGroups = Array.from(groupMap.values())
    .filter(({ type, modelId }) => modelId && type && complexGroups.has(type))
    .map(({ modelId }) => modelId);
  return componentsUsedAsGroups.length > new Set(componentsUsedAsGroups).size;
};

const getCanBeUsedAsComponentId = (
  isParentRelationAsReference: boolean,
  componentIds: ArdoqId[]
) => {
  if (!isParentRelationAsReference) {
    return () => true;
  }
  const includedComponentIds = new Set(componentIds);
  return (componentId: ArdoqId) => includedComponentIds.has(componentId);
};

type GetTraversalAndGroupingResultArgs = {
  graphInterface: GraphInterface;
  graph: GraphModelShape;
  componentIds: ArdoqId[];
  traversedReferenceIds: ArdoqId[];
  parentReferenceIds: Set<string>;
  groupingRules: GroupingRule[];
  isParentRelationAsReference: boolean;
  /**
   * Corresponds to `Features.GROUPING_RESEARCH` flag which is enabled for a
   * handful of customers, and cannot be removed easily.
   * In viewpoint mode this should always be false!
   */
  useNewGrouping: boolean;
  i18nLocale: I18nLocale;
  canBeUsedAsGroup?: (componentId: ArdoqId) => boolean;
};

const getTraversalAndGroupingResult = ({
  graphInterface,
  graph,
  componentIds,
  traversedReferenceIds,
  parentReferenceIds,
  groupingRules,
  isParentRelationAsReference,
  useNewGrouping,
  i18nLocale,
  canBeUsedAsGroup: customCanBeUsedAsGroup,
}: GetTraversalAndGroupingResultArgs): TraversalAndGroupingResult => {
  // buildGraphFromTraversalAndSearch provides a custom canBeUsedAsGroup
  const canBeUsedAsGroup =
    customCanBeUsedAsGroup ??
    getCanBeUsedAsComponentId(isParentRelationAsReference, componentIds);

  const {
    groupMap,
    errors,
    componentGroupsMap,
    componentMap,
    rootGroups,
    processedReferences,
    clonedComponentsMap,
  } = buildGroups({
    graphInterface,
    graph,
    componentIds,
    referenceIds: traversedReferenceIds,
    groupingRules,
    canBeUsedAsGroup,
    isParentRelationAsReference,
    useNewGrouping,
    i18nLocale,
  });

  const referenceList = getResultingReferenceList({
    traversedReferenceIds,
    processedReferences,
    parentReferenceIds,
    componentGroupsMap,
    clonedComponentsMap,
    graph,
  });

  const hasClones = clonedComponentsMap.size > 0 || groupMapHasClones(groupMap);

  return {
    componentMap,
    referenceList,
    groupMap,
    rootGroups,
    errors,
    hasClones,
  };
};

export { getTraversalAndGroupingResult };
